if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // Check if a trial variant is selected
        const trialVariantInput = this.form.querySelector('.trial-variant-id');
        const trialVariantId = trialVariantInput ? trialVariantInput.value : '';

        if (trialVariantId) {
          // Handle adding both primary and trial items
          this.addBothItemsToCart(trialVariantId);
        } else {
          // Standard single item add to cart
          this.addSingleItemToCart();
        }
      }

      addSingleItemToCart() {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        // Remove trial_variant_id from form data as it's not needed
        formData.delete('trial_variant_id');

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            this.handleCartResponse(response, formData);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.resetButtonState();
          });
      }

      async addBothItemsToCart(trialVariantId) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        try {
          // First, add the primary item
          const primaryFormData = new FormData(this.form);
          primaryFormData.delete('trial_variant_id');
          config.body = primaryFormData;

          const primaryResponse = await fetch(`${routes.cart_add_url}`, config);
          const primaryResult = await primaryResponse.json();

          if (primaryResult.status) {
            // Error adding primary item
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: primaryFormData.get('id'),
              errors: primaryResult.errors || primaryResult.description,
              message: primaryResult.message,
            });
            this.handleErrorMessage(primaryResult.description);
            this.error = true;
            this.resetButtonState();
            return;
          }

          // Second, add the trial item with a hidden property (underscore prefix hides from cart but shows in admin)
          const trialFormData = new FormData();
          trialFormData.append('id', trialVariantId);
          trialFormData.append('quantity', 1);
          trialFormData.append('properties[_Trial Item]', 'Yes');

          if (this.cart) {
            trialFormData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            trialFormData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }

          const trialConfig = fetchConfig('javascript');
          trialConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete trialConfig.headers['Content-Type'];
          trialConfig.body = trialFormData;

          const trialResponse = await fetch(`${routes.cart_add_url}`, trialConfig);
          const trialResult = await trialResponse.json();

          if (trialResult.status) {
            // Error adding trial item - but primary was added successfully
            // Still show cart with primary item
            console.warn('Trial item could not be added:', trialResult.description);
          }

          // Use the final response (from trial item) to update cart
          this.handleCartResponse(trialResult, primaryFormData);

        } catch (e) {
          console.error('Error adding items to cart:', e);
        } finally {
          this.resetButtonState();
        }
      }

      handleCartResponse(response, formData) {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButtonText.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          this.error = true;
          return;
        } else if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          });
        this.error = false;
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                this.cart.renderContents(response);
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          this.cart.renderContents(response);
        }
      }

      resetButtonState() {
        this.submitButton.classList.remove('loading');
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
